import React from 'react';

import { useCreateSupportTicketMutation } from '@edx/graphql/generated';
import { useValidationErrors } from '@edx/hooks/use-validation-errors';
import { useNotification } from '@edx/hooks/use-notification';
import { Button } from '@edx/components/button';
import { Textarea, TextInput } from '@edx/components/form';
import { ResponsiveImage } from '@edx/types/responsive-image';
import { Hero } from '@edx/components/hero';

type Props = {
  bgImage: ResponsiveImage;
  userFullName: string;
  userEmail: string;
};

export const ContactForm: React.FC<Props> = ({ bgImage, userFullName, userEmail }) => {
  const { showNotification } = useNotification();
  const [name, setName] = React.useState(userFullName);
  const [email, setEmail] = React.useState(userEmail);
  const [subject, setSubject] = React.useState('');
  const [body, setBody] = React.useState('');

  React.useEffect(() => {
    if (name === '' && userFullName) setName(userFullName);
    if (email === '' && userEmail) setEmail(userEmail);
  }, [name, email, userEmail, userFullName, setName, setEmail]);

  const [onContact, { loading: isSubmitting, error }] = useCreateSupportTicketMutation();
  const { errors: validationErrors } = useValidationErrors(error?.graphQLErrors);

  const handleInput = (setter: React.Dispatch<React.SetStateAction<string>>) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => setter(event.target.value);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    if (!isSubmitting) {
      await onContact({ variables: { input: { name, email, subject, body } } });

      setSubject('');
      setBody('');
      showNotification({ message: 'Petición enviada correctamente.' });
    }
  };

  return (
    <section>
      <Hero heading="Por si no encuentras respuestas a tus preguntas..." bgImage={bgImage} />

      <div className="relative max-w-6xl mx-auto px-4 md:px-10 py-1 md:py-4 mb-10 -mt-14">
        <div className="grid grid-cols-12">
          <div className="col-start-1 sm:col-start-2 md:col-start-3 lg:col-start-4 col-span-12 sm:col-span-10 md:col-span-8 lg:col-span-6 bg-white rounded font-body text-gray-700">
            <div className="p-6 md:p-10">
              <form className="grid grid-cols-12 text-gray-600" method="post" onSubmit={handleSubmit}>
                <TextInput
                  variant="light"
                  className="mt-1 font-medium"
                  label="Nombre"
                  labelClassName="col-span-12 flex flex-col mb-4"
                  type="text"
                  id="name"
                  name="name"
                  error={validationErrors.name}
                  onChange={handleInput(setName)}
                  value={name}
                  required
                />

                <TextInput
                  variant="light"
                  className="mt-1 font-medium"
                  label="Email"
                  labelClassName="col-span-12 flex flex-col mb-4"
                  type="email"
                  id="email"
                  name="email"
                  error={validationErrors.email}
                  onChange={handleInput(setEmail)}
                  value={email}
                  required
                />

                <TextInput
                  variant="light"
                  className="mt-1 font-medium"
                  label="Asunto"
                  labelClassName="col-span-12 flex flex-col mb-4"
                  type="text"
                  id="subject"
                  name="subject"
                  error={validationErrors.subject}
                  onChange={handleInput(setSubject)}
                  value={subject}
                  required
                />

                <Textarea
                  variant="light"
                  className="mt-1 font-medium"
                  label="Mensaje"
                  labelClassName="col-span-12 flex flex-col mb-4"
                  id="body"
                  name="text"
                  error={validationErrors.body}
                  onChange={handleInput(setBody)}
                  value={body}
                  required
                />

                <div className="mt-4 col-span-12">
                  <Button type="submit" textVariant="medium" disabled={isSubmitting}>
                    Enviar
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
